import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';

export interface FieldTripRecord {
  id: string;
  project_number: string;
  customer_id: string;
  location_id: string | null;
  scope: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  po_number: string | null;
  solution_types: string[] | null;
  created_at: string;
  updated_at: string;
  location: { id: string; name: string } | null;
}

/**
 * Field trips for the current account scope.
 *
 * No join to `users`: the lead technician is internal staff identity. A
 * customer session cannot resolve that join (RLS) while a staff session
 * viewing the portal could, so the column is left out rather than relying
 * on RLS to blank it.
 */
export function useFieldTrips() {
  const scope = useCustomerScope();

  return useQuery({
    queryKey: ['field-trips', scope.key],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<FieldTripRecord[]> => {
      const { data, error } = await supabase
        .from('field_trips')
        .select(`
          id, project_number, customer_id, location_id, scope, status,
          start_date, end_date, po_number, solution_types,
          created_at, updated_at,
          location:locations(id, name)
        `)
        .in('customer_id', scope.ids)
        .in('status', ['scheduled', 'in_progress', 'completed'])
        .order('start_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as FieldTripRecord[];
    },
    staleTime: 2 * 60 * 1000,
  });
}
